import type { ReactNode } from 'react';

import type { SearchImpactSummary } from '~/features/catalog-operations/types/search-impact.types';
import { cn } from '~/utils/cn';

import { formatVolume } from './search-impact-utils';

interface Props {
  summary: SearchImpactSummary;
  /** Open (undismissed) review items — kept in step with the panel below. */
  openReviewCount: number;
  /** Descriptors held back from the content, for the reached-card note. */
  withheldCount: number;
}

/** One tile: label, headline figure, and a one-line explanation. */
function Tile({
  label,
  note,
  testId,
  children,
}: {
  label: string;
  note: string;
  /** Tile `data-testid`; the figure row gets `${testId}-value`, the note `${testId}-note`. */
  testId: string;
  children: ReactNode;
}) {
  return (
    <div className="bg-white p-5" data-testid={testId}>
      <div className="text-muted-foreground text-xs font-semibold">{label}</div>
      <div
        className="mt-2 flex flex-wrap items-baseline gap-2 text-[27px] leading-none font-semibold tabular-nums"
        data-testid={`${testId}-value`}
      >
        {children}
      </div>
      <p
        className="text-muted-foreground mt-2 text-xs leading-relaxed"
        data-testid={`${testId}-note`}
      >
        {note}
      </p>
    </div>
  );
}

/** "before → after" pair: the source figure quieter, the enriched one in the accent. */
function BeforeAfter({
  before,
  after,
  testId,
}: {
  before: ReactNode;
  after: ReactNode;
  testId: string;
}) {
  return (
    <>
      <span
        className="text-warning text-[17px] font-semibold"
        data-testid={`${testId}-before-value`}
      >
        {before}
      </span>
      <span className="text-muted-foreground text-[15px]" aria-hidden="true">
        →
      </span>
      <span className="text-primary" data-testid={`${testId}-after-value`}>
        {after}
      </span>
    </>
  );
}

/** Small "↑ +2" chip; hidden when nothing changed. */
function Delta({ value, testId }: { value: number; testId: string }) {
  if (value === 0) return null;
  return (
    <span
      data-testid={testId}
      className={cn(
        'rounded-md px-2 py-0.5 text-xs font-semibold',
        value > 0 ? 'bg-success-50 text-success' : 'bg-muted text-muted-foreground',
      )}
    >
      {value > 0 ? `↑ +${String(value)}` : `↓ ${String(value)}`}
    </span>
  );
}

/** Quieter suffix after a headline figure ("of 6", "/mo"). */
function Suffix({ children }: { children: ReactNode }) {
  return <span className="text-muted-foreground text-[15px] font-semibold">{children}</span>;
}

/**
 * Four headline tiles: descriptors reached, search volume covered, attributes and
 * needs review — each before → after where it applies. Figures come from the
 * server's summary, so the cards can't disagree with the tables below.
 */
export function SearchImpactStatCards({ summary, openReviewCount, withheldCount }: Props) {
  const { descriptorsReached: reached, volumeCovered: volume, attributes } = summary;
  const reachedNote =
    withheldCount > 0
      ? `The same matcher run on the source content and on the enriched content. ${String(withheldCount)} not placed, see review.`
      : 'The same matcher run on the source content and on the enriched content.';
  const attributeNote = `${String(attributes.gained)} new, ${String(attributes.lost)} removed. ${String(
    attributes.renamed + attributes.merged,
  )} keys renamed or merged, no information change.`;

  return (
    <section
      className="bg-border grid grid-cols-1 gap-px overflow-hidden rounded-2xl border shadow-sm sm:grid-cols-2 lg:grid-cols-4"
      data-testid="search-impact-stat-cards"
    >
      <Tile
        label="Search descriptors reached"
        note={reachedNote}
        testId="search-impact-stat-descriptors-card"
      >
        <BeforeAfter
          before={reached.before}
          after={reached.after}
          testId="search-impact-stat-descriptors"
        />
        <Suffix>of {reached.total}</Suffix>
        <Delta
          value={reached.after - reached.before}
          testId="search-impact-stat-descriptors-delta-badge"
        />
      </Tile>
      <Tile
        testId="search-impact-stat-volume-card"
        label="Search volume covered"
        note="Monthly searches behind the descriptors the content now covers. Exposure, not a traffic forecast."
      >
        <BeforeAfter
          before={formatVolume(volume.before)}
          after={formatVolume(volume.after)}
          testId="search-impact-stat-volume"
        />
        <Suffix>/mo</Suffix>
      </Tile>
      <Tile label="Attributes" note={attributeNote} testId="search-impact-stat-attributes-card">
        <BeforeAfter
          before={attributes.before}
          after={attributes.after}
          testId="search-impact-stat-attributes"
        />
        <Delta
          value={attributes.after - attributes.before}
          testId="search-impact-stat-attributes-delta-badge"
        />
      </Tile>
      <Tile
        testId="search-impact-stat-needs-review-card"
        label="Needs review"
        note={
          openReviewCount > 0
            ? 'Items below for a person to check before this product publishes.'
            : 'Nothing for a person to check.'
        }
      >
        <span
          className={openReviewCount > 0 ? 'text-warning' : 'text-success'}
          data-testid="search-impact-stat-needs-review-count-value"
        >
          {openReviewCount}
        </span>
      </Tile>
    </section>
  );
}
